import React from 'react';
import { useTranslation } from 'react-i18next';
import { appLink } from '../../common/appLinks';
import { currentUserToken, getCurrentUser, startProCheckout } from '../../common/auth';
import { consumeCheckoutIntent, fetchPlanAction } from '../../common/checkout';
import { BILLING_EMAIL } from '../../common/constants';
import { useSessionToken } from '../../common/useSignedIn';

const profileURL = () => appLink('termBrowser', getCurrentUser()?.url || '/');

// Where online-api's answer sends a Pro button, or null when it can't (no answer, or Community
// with no checkout configured). `label` is set only when it replaces the button's own.
const destination = (result, t) => {
  if (result?.action)
    return result.action.kind === 'subscribe'
      ? { href: result.action.url, toCheckout: true }
      : { href: result.action.url, label: result.action.label };
  if (result?.plan && result.plan !== 'community')
    return { href: profileURL(), label: t('pricing.plans.pro.cta_has_plan') };
  return null;
};

// What the Pro buttons (the hero's and the Pro card's) do, from online-api's GET /billing/plan/
// (common/checkout.js; OpenConceptLab/ocl_online#325):
// - signed out: sign in, come back here, and go on where the button would have gone;
// - Community: the store's checkout, tied to the account;
// - Pro bought in the store: its portal, under online-api's label ("Cancel or change your subscription");
// - a plan OCL set up by hand: the account's page in OCL Online ("See your plan").
// Until online-api answers, or if it can't, the button asks again when clicked, and says so if
// checkout still can't open. Answers belong to the token and language they were asked with, so
// another account's never shows, nor a label in the previous language.
// Returns the button's {href, onClick}, `label` when it replaces the button's own, `toCheckout` when
// the button leads to checkout (the terms line goes with it), and `error`.
const useProCheckout = () => {
  const { t, i18n } = useTranslation();
  const token = useSessionToken();
  const [answers, setAnswers] = React.useState({});
  const [error, setError] = React.useState(false);
  const resume = React.useRef(false);
  const lang = i18n.resolvedLanguage || 'en';
  const key = `${lang} ${token}`;

  // Each answer is kept under the language and token it was asked with, whatever order answers
  // arrive in. One for a token no longer signed in resolves to null, so nothing follows it.
  const ask = () => fetchPlanAction(token, lang).then(result => {
    setAnswers(current => ({ ...current, [key]: result }));
    return token === currentUserToken() ? result : null;
  });

  const follow = result => {
    const next = destination(result, t);
    if (next)
      window.location.assign(next.href);
    else
      setError(true);
  };

  React.useEffect(() => { resume.current = consumeCheckoutIntent(); }, []);

  React.useEffect(() => {
    setError(false);
    if (!token)
      return undefined;
    let live = true;
    ask().then(result => {
      if (!live || !resume.current)
        return;
      resume.current = false;
      follow(result);
    });
    return () => { live = false; };
  }, [token, lang]);

  if (!token)
    return { href: '/pricing', onClick: startProCheckout, toCheckout: true };
  const next = destination(answers[key], t);
  if (next)
    return next;
  const retry = (event) => {
    event.preventDefault();
    setError(false);
    ask().then(follow);
  };
  return { href: '#', onClick: retry, toCheckout: true, error: error ? t('pricing.checkout_error', { email: BILLING_EMAIL }) : null };
};

export default useProCheckout;
